import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  CheckCircle2,
  Clock,
  Loader2,
  MessageCircle,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { SERVICES, whatsappLink } from "../data/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIES = ["Perro", "Gato", "Conejo", "Ave", "Reptil", "Otro"];

function formatISO(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function BookingPage() {
  const navigate = useNavigate();
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [service, setService] = useState("");
  const [petSpecies, setPetSpecies] = useState("");
  const [form, setForm] = useState({
    owner_name: "",
    email: "",
    phone: "",
    pet_name: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Availability state
  const [holidays, setHolidays] = useState([]); // list of ISO dates
  const [availability, setAvailability] = useState(null); // { all_slots, taken_times, available_times, is_holiday, holiday_reason }
  const [loadingAvail, setLoadingAvail] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // Load holidays once
  useEffect(() => {
    axios
      .get(`${API}/holidays`)
      .then((r) => setHolidays(r.data.map((h) => h.date)))
      .catch(() => setHolidays([]));
  }, []);

  // Fetch availability when date changes
  const fetchAvailability = useCallback(async (d) => {
    if (!d) {
      setAvailability(null);
      return;
    }
    try {
      setLoadingAvail(true);
      setTime("");
      const res = await axios.get(`${API}/availability`, {
        params: { date: formatISO(d) },
      });
      setAvailability(res.data);
    } catch (err) {
      console.error(err);
      setAvailability(null);
    } finally {
      setLoadingAvail(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailability(date);
  }, [date, fetchAvailability]);

  const isDateDisabled = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    return holidays.includes(formatISO(d));
  };

  const allSlots = availability?.all_slots || [];
  const takenSet = new Set(availability?.taken_times || []);
  const dateSelected = !!date;
  const isHoliday = !!availability?.is_holiday;

  const canSubmit =
    form.owner_name &&
    form.email &&
    form.phone &&
    form.pet_name &&
    petSpecies &&
    date &&
    time &&
    service &&
    !isHoliday;

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Completa todos los campos requeridos.");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        owner_name: form.owner_name,
        email: form.email,
        phone: form.phone,
        pet_name: form.pet_name,
        pet_species: petSpecies,
        service,
        date: formatISO(date),
        time,
        notes: form.notes || null,
      };
      const res = await axios.post(`${API}/bookings`, payload);
      setSuccess(res.data);
      toast.success("¡Reserva enviada! Te confirmaremos pronto.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail) {
        toast.error(detail);
        // Refresh availability to update UI
        fetchAvailability(date);
      } else {
        toast.error("No pudimos crear la reserva. Intenta nuevamente.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App" data-testid="booking-page">
      <Navbar />
      <main className="pt-28 pb-20">
        <section className="mx-auto max-w-5xl px-6 lg:px-12">
          <Link
            to="/"
            data-testid="booking-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" /> Volver al inicio
          </Link>

          <div className="mt-5 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#EA580C]">
                Reservar hora
              </span>
              <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                Agenda una atención para tu mascota
              </h1>
              <p className="mt-3 max-w-2xl text-slate-600">
                Selecciona el servicio, la fecha y completa los datos. Las
                horas tomadas y feriados aparecen bloqueados.
              </p>
            </div>
            <a
              href={whatsappLink("Hola, prefiero reservar por WhatsApp")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <MessageCircle className="h-4 w-4 text-[#25D366]" /> Reservar por
              WhatsApp
            </a>
          </div>

          {success ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="booking-success"
              className="mt-10 rounded-3xl border border-emerald-200 bg-emerald-50 p-8 text-center"
            >
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-500 text-white">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h2 className="mt-5 font-display text-2xl font-medium text-slate-900">
                ¡Reserva enviada con éxito!
              </h2>
              <p className="mt-2 text-slate-600">
                Hemos recibido tu solicitud para{" "}
                <strong>{success.service}</strong> el{" "}
                <strong>
                  {success.date} a las {success.time}
                </strong>
                . Te enviamos una copia a {success.email} y te contactaremos
                pronto.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button
                  onClick={() => navigate("/")}
                  className="rounded-full bg-[#1E3A8A] text-white"
                >
                  Volver al inicio
                </Button>
                <a
                  href={whatsappLink(
                    `Hola, acabo de reservar para ${success.pet_name} el ${success.date} a las ${success.time}.`
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <MessageCircle className="h-4 w-4 text-[#25D366]" /> Confirmar
                  por WhatsApp
                </a>
              </div>
            </motion.div>
          ) : (
            <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
              {/* Step 1: Date / Service */}
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-7">
                <div className="flex items-center gap-2 text-sm font-medium text-[#1E3A8A]">
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-[#1E3A8A] text-white">
                    1
                  </span>
                  Servicio y fecha
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Label>Servicio</Label>
                    <Select value={service} onValueChange={setService}>
                      <SelectTrigger
                        data-testid="booking-service-select"
                        className="mt-1.5 rounded-xl"
                      >
                        <SelectValue placeholder="Selecciona un servicio" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICES.map((s) => (
                          <SelectItem key={s.id} value={s.title}>
                            {s.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-5">
                  <Label className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-[#1E3A8A]" /> Fecha
                  </Label>
                  <div className="mt-1.5 rounded-2xl border border-slate-200">
                    <Calendar
                      data-testid="booking-calendar"
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={isDateDisabled}
                      className="mx-auto"
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Los feriados ({holidays.length} cargados) aparecen
                    deshabilitados.
                  </p>
                </div>

                <div className="mt-5">
                  <Label className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#1E3A8A]" /> Hora disponible
                  </Label>

                  {!dateSelected && (
                    <p className="mt-2 text-sm text-slate-500">
                      Selecciona una fecha para ver las horas disponibles.
                    </p>
                  )}

                  {dateSelected && loadingAvail && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin" /> Cargando
                      disponibilidad...
                    </div>
                  )}

                  {dateSelected && !loadingAvail && isHoliday && (
                    <div
                      data-testid="booking-holiday-notice"
                      className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                    >
                      <AlertTriangle className="mt-0.5 h-4 w-4" />
                      <div>
                        <strong>Feriado:</strong>{" "}
                        {availability?.holiday_reason || "Día no laboral"}.
                        Selecciona otro día para reservar.
                      </div>
                    </div>
                  )}

                  {dateSelected && !loadingAvail && !isHoliday && (
                    <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-5">
                      {allSlots.map((t) => {
                        const taken = takenSet.has(t);
                        const active = time === t;
                        return (
                          <button
                            type="button"
                            key={t}
                            disabled={taken}
                            onClick={() => !taken && setTime(t)}
                            data-testid={`booking-time-${t}`}
                            className={`rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                              taken
                                ? "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300 line-through"
                                : active
                                  ? "border-[#1E3A8A] bg-[#1E3A8A] text-white"
                                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                            }`}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {dateSelected && !loadingAvail && !isHoliday && allSlots.length > 0 && (
                    <p className="mt-2 text-xs text-slate-500">
                      {takenSet.size} de {allSlots.length} horas ocupadas
                    </p>
                  )}
                </div>
              </div>

              {/* Step 2: Owner / Pet */}
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-5">
                <div className="flex items-center gap-2 text-sm font-medium text-[#1E3A8A]">
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-[#1E3A8A] text-white">
                    2
                  </span>
                  Tus datos y tu mascota
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4">
                  <div>
                    <Label htmlFor="b-owner">Nombre del tutor</Label>
                    <Input
                      id="b-owner"
                      name="owner_name"
                      value={form.owner_name}
                      onChange={handleChange}
                      placeholder="Tu nombre completo"
                      className="mt-1.5 rounded-xl"
                      data-testid="booking-input-owner-name"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="b-email">Email</Label>
                      <Input
                        id="b-email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="tu@email.com"
                        className="mt-1.5 rounded-xl"
                        data-testid="booking-input-email"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="b-phone">Teléfono</Label>
                      <Input
                        id="b-phone"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+56 ..."
                        className="mt-1.5 rounded-xl"
                        data-testid="booking-input-phone"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="b-pet">Nombre de la mascota</Label>
                      <Input
                        id="b-pet"
                        name="pet_name"
                        value={form.pet_name}
                        onChange={handleChange}
                        placeholder="Ej: Luna"
                        className="mt-1.5 rounded-xl"
                        data-testid="booking-input-pet-name"
                        required
                      />
                    </div>
                    <div>
                      <Label>Especie</Label>
                      <Select value={petSpecies} onValueChange={setPetSpecies}>
                        <SelectTrigger
                          data-testid="booking-species-select"
                          className="mt-1.5 rounded-xl"
                        >
                          <SelectValue placeholder="Selecciona" />
                        </SelectTrigger>
                        <SelectContent>
                          {SPECIES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="b-notes">
                      Comentarios (opcional)
                    </Label>
                    <Textarea
                      id="b-notes"
                      name="notes"
                      value={form.notes}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Síntomas, motivo de visita..."
                      className="mt-1.5 rounded-xl"
                      data-testid="booking-input-notes"
                    />
                  </div>

                  <Button
                    type="button"
                    onClick={submit}
                    disabled={!canSubmit || submitting}
                    data-testid="booking-submit-btn"
                    className="mt-2 rounded-full bg-[#EA580C] text-white hover:bg-[#c2410c]"
                  >
                    {submitting ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <CalendarIcon className="mr-2 h-4 w-4" />
                    )}
                    Confirmar reserva
                  </Button>
                  {!canSubmit && (
                    <p className="text-xs text-slate-500">
                      Completa servicio, fecha, hora y todos tus datos.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
